import React, { useEffect, useState } from 'react';
import { Inputs, DEFAULT_INPUTS } from '../utils/roiCalculations';
import { ResultsPage } from './ResultsPage';
export function PreviewEditor() {
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [showPreview, setShowPreview] = useState(true);
  // Load initial inputs from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('previewInputs');
    if (stored) {
      try {
        setInputs(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse preview inputs', e);
      }
    }
  }, []);
  // Save inputs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('previewInputs', JSON.stringify(inputs));
  }, [inputs]);
  const handleChange = (field: keyof Inputs, value: any) => {
    setInputs({
      ...inputs,
      [field]: value
    });
  };
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    if (value === '') {
      setInputs({
        ...inputs,
        [field]: ''
      });
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setInputs({
        ...inputs,
        [field]: numValue
      });
    }
  };
  return <div className="min-h-screen bg-gray-100" data-id="element-787">
      {/* Split View */}
      <div className="flex h-screen" data-id="element-788">
        {/* Editor Panel - Left Side */}
        <div className="w-96 bg-white border-r border-gray-300 overflow-y-auto" data-id="element-789">
          <div className="p-6 border-b border-gray-200 bg-navy text-white sticky top-0 z-10" data-id="element-790">
            <h1 className="text-xl font-bold" data-id="element-791">Preview Editor</h1>
            <p className="text-sm text-blue-200 mt-1" data-id="element-792">
              Changes sync to /results in real-time
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-793">
            {/* Company Info */}
            <div data-id="element-794">
              <h3 className="font-bold text-navy mb-3" data-id="element-795">Company Info</h3>
              <div className="space-y-3" data-id="element-796">
                <div data-id="element-797">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-798">
                    Company Name
                  </label>
                  <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-799" />
                </div>
                <div data-id="element-800">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-801">
                    Email
                  </label>
                  <input type="email" value={inputs.email} onChange={e => handleChange('email', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-802" />
                </div>
              </div>
            </div>

            {/* Recruiting Budget */}
            <div data-id="element-803">
              <h3 className="font-bold text-navy mb-3" data-id="element-804">Recruiting Budget</h3>
              <div className="space-y-3" data-id="element-805">
                <div data-id="element-806">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-807">
                    Monthly Ad Spend ($)
                  </label>
                  <input type="number" min="0" step="500" value={inputs.adSpend} onChange={e => handleNumericChange('adSpend', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-808" />
                </div>
                <div data-id="element-809">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-810">
                    Cost Per Hire ($)
                  </label>
                  <input type="number" min="1" step="50" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-811" />
                </div>
                <div data-id="element-812">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-813">
                    Recruiter Hourly Rate ($)
                  </label>
                  <input type="number" min="0" step="5" value={inputs.recruiterHourlyRate} onChange={e => handleNumericChange('recruiterHourlyRate', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-814" />
                </div>
              </div>
            </div>

            {/* Revenue & Margins */}
            <div data-id="element-815">
              <h3 className="font-bold text-navy mb-3" data-id="element-816">Revenue & Margins</h3>
              <div className="space-y-3" data-id="element-817">
                <div data-id="element-818">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-819">
                    Average Deal Size ($)
                  </label>
                  <input type="number" min="0" step="1000" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-820" />
                </div>
                <div data-id="element-821">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-822">
                    Gross Profit Margin (%)
                  </label>
                  <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-823" />
                </div>
              </div>
            </div>

            {/* Sales Performance */}
            <div data-id="element-824">
              <h3 className="font-bold text-navy mb-3" data-id="element-825">Sales Performance</h3>
              <div className="space-y-3" data-id="element-826">
                <div data-id="element-827">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-828">
                    Sales in First 90 Days (Total)
                  </label>
                  <input type="number" min="0" step="1" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-829" />
                </div>
                <div data-id="element-830">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-831">
                    Monthly Sales (Veteran Reps)
                  </label>
                  <input type="number" min="0" step="1" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-832" />
                </div>
                <div data-id="element-833">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-834">
                    Deal Close Rate (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-835" />
                </div>
              </div>
            </div>

            {/* Retention Rates */}
            <div data-id="element-836">
              <h3 className="font-bold text-navy mb-3" data-id="element-837">Retention Rates</h3>
              <div className="space-y-3" data-id="element-838">
                <div data-id="element-839">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-840">
                    Hired to Launched (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-841" />
                </div>
                <div data-id="element-842">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-843">
                    Launched to Day 30 (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-844" />
                </div>
                <div data-id="element-845">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-846">
                    Day 30 to Day 60 (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pct60} onChange={e => handleNumericChange('pct60', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-847" />
                </div>
                <div data-id="element-848">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-849">
                    Day 60 to Day 90 (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-850" />
                </div>
                <div data-id="element-851">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-852">
                    Day 90 to 1 Year (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-853" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Panel - Right Side */}
        <div className="flex-1 overflow-y-auto bg-gray-50" data-id="element-854">
          <div className="sticky top-0 z-10 bg-white border-b border-gray-300 px-6 py-3 flex items-center justify-between" data-id="element-855">
            <div className="flex items-center gap-2" data-id="element-856">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" data-id="element-857"></div>
              <span className="text-sm font-semibold text-gray-700" data-id="element-858">
                Live Preview
              </span>
            </div>
            <a href="/results" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 font-semibold" data-id="element-859">
              Open in New Tab →
            </a>
          </div>
          <ResultsPage isPreview={true} data-id="element-860" />
        </div>
      </div>
    </div>;
}